/**
 * Nominal GDP, real GDP and the GDP deflator.
 *
 * The three are tied by one identity: real GDP = nominal GDP ÷ (deflator ÷ 100), where
 * the deflator is 100 in the base (reference) year. Growth rates compound the same way:
 * 1 + nominal growth = (1 + real growth) × (1 + inflation).
 */

/** Which two quantities the reader has for each period. */
export type Known = "nominal-deflator" | "real-deflator" | "nominal-real";

export interface PeriodInput {
  label: string;
  /** The first known quantity (see `Known`). */
  first: number;
  /** The second known quantity. */
  second: number;
  /** Population in people, or null when not given. */
  population: number | null;
}

export interface PeriodResult {
  label: string;
  nominal: number;
  real: number;
  deflator: number;
  /** Real GDP per person, in the units of GDP × scale ÷ people; null without population. */
  realPerCapita: number | null;
  /** Change from the previous period, as decimals; null for the first period. */
  nominalGrowth: number | null;
  realGrowth: number | null;
  inflation: number | null;
  perCapitaGrowth: number | null;
}

export function realFromNominal(nominal: number, deflator: number): number {
  return (nominal / deflator) * 100;
}

export function nominalFromReal(real: number, deflator: number): number {
  return (real * deflator) / 100;
}

export function deflatorFrom(nominal: number, real: number): number {
  return (nominal / real) * 100;
}

/** (current − previous) ÷ previous. */
export function growthRate(previous: number, current: number): number {
  return (current - previous) / previous;
}

function triple(known: Known, a: number, b: number) {
  if (known === "nominal-deflator") {
    return { nominal: a, deflator: b, real: realFromNominal(a, b) };
  }
  if (known === "real-deflator") {
    return { real: a, deflator: b, nominal: nominalFromReal(a, b) };
  }
  return { nominal: a, real: b, deflator: deflatorFrom(a, b) };
}

/** True when both quantities are finite and above zero, which every formula needs. */
export function validPeriod(first: number, second: number): boolean {
  return (
    Number.isFinite(first) && Number.isFinite(second) && first > 0 && second > 0
  );
}

/**
 * Fill in nominal GDP, real GDP and the deflator for every period and the growth
 * between consecutive periods. `scale` is how many currency units one entered unit is
 * worth (1e9 for billions); population is in people.
 */
export function analysePeriods(
  known: Known,
  periods: readonly PeriodInput[],
  scale: number
): PeriodResult[] {
  const results: PeriodResult[] = [];
  for (const period of periods) {
    const { nominal, real, deflator } = triple(
      known,
      period.first,
      period.second
    );
    const realPerCapita =
      period.population !== null && period.population > 0
        ? (real * scale) / period.population
        : null;
    const before = results.at(-1);
    results.push({
      label: period.label,
      nominal,
      real,
      deflator,
      realPerCapita,
      nominalGrowth: before ? growthRate(before.nominal, nominal) : null,
      realGrowth: before ? growthRate(before.real, real) : null,
      inflation: before ? growthRate(before.deflator, deflator) : null,
      perCapitaGrowth:
        before && before.realPerCapita !== null && realPerCapita !== null
          ? growthRate(before.realPerCapita, realPerCapita)
          : null,
    });
  }
  return results;
}
